// Shared with the frontend welcome tour and help article 917.
export const CHATGPT_PLUGIN_URL =
  'https://chatgpt.com/plugins/plugin_asdk_app_6a7ffe2c90c081918c5d962bd6f1b3c4';

export const chatGPTContent = {
  en: {
    title: 'Your business. One conversation.',
    description:
      'Connect OmceanBooking to ChatGPT. Explore your schedule, understand booking trends and make supported updates with your approval, right in the conversation.',
    eyebrow: 'OmceanBooking in ChatGPT',
    connect: 'Connect on ChatGPT',
    setupLink: 'See how to connect',
    prerequisite:
      'For business owners and staff with an OmceanBooking account.',
    promptsTitle: 'Start with a question you already have.',
    promptsDescription:
      'Ask in English or Traditional Chinese. Include the dates, location or class you mean, then keep the conversation going.',
    prompts: [
      {
        category: 'Plan your day',
        title: 'Get the full picture before the first class.',
        prompt:
          "@OmceanBooking Show today's classes with their times, locations, instructors, bookings and waitlists.",
        detail:
          'Bring the day’s schedule and attendance numbers into one conversation.',
        access: 'Read only',
      },
      {
        category: 'Spot demand',
        title: 'See where another class could help.',
        prompt:
          '@OmceanBooking Find classes this week that are full or have waitlists, and suggest where I may need more capacity.',
        detail:
          'Use your booking data to explore where demand is outgrowing the timetable.',
        access: 'Read only',
      },
      {
        category: 'Understand your business',
        title: 'Turn recent activity into a useful summary.',
        prompt:
          '@OmceanBooking Summarize new customers, bookings and pass usage from the last 30 days.',
        detail:
          'Review the patterns, then ask follow-up questions about what stands out.',
        access: 'Read only',
      },
      {
        category: 'Make an update',
        title: 'Move from a question to an approved change.',
        prompt:
          "@OmceanBooking Change the capacity of Friday's 7pm class to 12.",
        detail:
          'Requires change access and the relevant staff permission. Review the exact class and proposed change before confirming.',
        access: 'Your confirmation required',
      },
    ],
    setupTitle: 'Connect once. Ask naturally.',
    setupDescription:
      'Use a ChatGPT account with access to plugins and an OmceanBooking login with an active staff role.',
    steps: [
      {
        title: 'Open OmceanBooking in ChatGPT',
        text: 'Open the listing, choose “Try in chat”, then select “Sign in with OmceanBooking”.',
      },
      {
        title: 'Choose your business and access',
        text: 'Sign in with Google, LINE, or email and password. Select one business and choose “Read only” or “Read and make changes”.',
      },
      {
        title: 'Start the conversation',
        text: 'Return to ChatGPT and mention @OmceanBooking. Ask your first question with the dates, location or records you need.',
      },
    ],
    screenshotAlt:
      'OmceanBooking’s ChatGPT listing with Try in chat and example prompts in English and Traditional Chinese',
    screenshotCaption: 'Find OmceanBooking in the ChatGPT directory.',
    guide: 'Read the complete connection guide',
    controlTitle: 'Your business. Your permissions.',
    controlDescription:
      'Choose how ChatGPT can help. Every connection stays within your selected business and existing staff role.',
    controls: [
      {
        title: 'Read only',
        text: 'Look up information, explore reports and search OmceanBooking help. This access cannot create, update or delete records.',
      },
      {
        title: 'Read and make changes',
        text: 'Allow supported admin updates. ChatGPT asks you to confirm the proposed action, and your staff permissions still apply.',
      },
      {
        title: 'Visible in your activity log',
        text: 'Changes made through the connection are identified as ChatGPT in OmceanBooking’s activity log, so your team can see where an update came from.',
      },
    ],
    faqTitle: 'Before your first conversation',
    faqs: [
      {
        question: 'Is this for my staff or my customers?',
        answer:
          'This integration is for business owners and staff. Connecting requires an active staff role in the selected OmceanBooking business. Customer booking conversations on your website and LINE are separate OmceanBooking features.',
      },
      {
        question: 'Can I connect more than one business?',
        answer:
          'Each connected account is bound to one business. Connect each business separately and check which account you are using before asking a question or approving a change.',
      },
      {
        question: 'What can’t I change through ChatGPT?',
        answer:
          'The connector does not expose financial balance or payment changes, staff access changes, free-form customer messages or subscription cancellation. The dedicated customer health field is also unavailable. Available tools depend on your role, booking modules and selected access level.',
      },
      {
        question: 'Can I disconnect later?',
        answer:
          'Yes. In ChatGPT, open Settings → Plugins → OmceanBooking to review connected accounts or disconnect. Disconnecting prevents future access; it does not undo changes you already approved.',
      },
      {
        question: 'What if I can’t find or use the integration?',
        answer:
          'Check that plugins are available for your ChatGPT account or workspace. Sign in with an OmceanBooking account that has an active staff role, select the correct business and review your access level. Your account admin can help check role permissions.',
      },
    ],
    closingTitle: 'Bring your next business question to ChatGPT.',
    closingText:
      'Connect your account, choose your access and start with a question about today.',
    newCustomer: 'New to OmceanBooking?',
    contact: 'Talk to our team',
  },
  zh: {
    title: '一句提問，掌握商家日常。',
    description:
      '將 OmceanBooking 連結至 ChatGPT。在對話中查看課表、了解預約趨勢，並在您核准後執行支援的更新。',
    eyebrow: '在 ChatGPT 使用 OmceanBooking',
    connect: '連結至 ChatGPT',
    setupLink: '了解連結方式',
    prerequisite: '適用於擁有 OmceanBooking 帳號的經營者與員工。',
    promptsTitle: '從您每天都會問的問題開始。',
    promptsDescription:
      '使用繁體中文或英文，說明日期、地點或課程，再接著追問您想了解的細節。',
    prompts: [
      {
        category: '安排今天的工作',
        title: '第一堂課開始前，掌握今日安排。',
        prompt:
          '@OmceanBooking 幫我查看今天的課程安排，列出時間、地點、教練、預約人數和候補人數。',
        detail: '在同一段對話中，查看當天課表與預約、候補人數。',
        access: '唯讀即可使用',
      },
      {
        category: '掌握課程需求',
        title: '看看哪些時段值得增加名額。',
        prompt:
          '@OmceanBooking 找出本週已額滿或有候補名單的課程，並建議哪些時段可能需要增加名額。',
        detail: '根據實際預約資料，了解哪些時段的需求超過現有名額。',
        access: '唯讀即可使用',
      },
      {
        category: '了解營運狀況',
        title: '將近期活動整理成實用摘要。',
        prompt:
          '@OmceanBooking 分析最近 30 天的新顧客、預約與課卡使用情況，並摘要值得注意的趨勢。',
        detail: '先掌握整體趨勢，再針對值得注意的變化繼續提問。',
        access: '唯讀即可使用',
      },
      {
        category: '執行日常更新',
        title: '從提問到變更，每一步由您確認。',
        prompt: '@OmceanBooking 將星期五晚上七點課程的名額改為 12 人。',
        detail:
          '需要變更權限及相關員工權限。確認前，請核對課程與預計修改的內容。',
        access: '需要您的確認',
      },
    ],
    setupTitle: '完成連結，就能直接提問。',
    setupDescription:
      '準備可使用外掛程式的 ChatGPT 帳號，以及在商家中具有有效員工角色的 OmceanBooking 帳號。',
    steps: [
      {
        title: '在 ChatGPT 開啟 OmceanBooking',
        text: '開啟外掛程式頁面，選擇「Try in chat」，再選擇「Sign in with OmceanBooking」。',
      },
      {
        title: '選擇商家與存取層級',
        text: '使用 Google、LINE 或電子郵件與密碼登入。選擇一個商家，再選擇「唯讀」或「讀取及變更」。',
      },
      {
        title: '開始對話',
        text: '返回 ChatGPT，提及 @OmceanBooking。提出問題，並提供需要查詢的日期、地點或紀錄。',
      },
    ],
    screenshotAlt:
      'OmceanBooking 的 ChatGPT 外掛程式頁面，顯示 Try in chat 以及繁體中文和英文範例提示',
    screenshotCaption: '在 ChatGPT 外掛程式目錄中找到 OmceanBooking。',
    guide: '閱讀完整連結教學',
    controlTitle: '您的商家，由您掌握權限。',
    controlDescription:
      '自行決定 ChatGPT 可以如何協助。每個連線都只限於所選商家，並遵守您原有的員工角色權限。',
    controls: [
      {
        title: '唯讀',
        text: '查詢資料、查看報表及搜尋 OmceanBooking 操作說明。此層級無法新增、更新或刪除紀錄。',
      },
      {
        title: '讀取及變更',
        text: '允許支援的管理更新。ChatGPT 會請您確認預計執行的操作，您的員工權限仍然適用。',
      },
      {
        title: '變更來源清楚可查',
        text: '透過此連線進行的變更，會在 OmceanBooking 活動紀錄中標示為 ChatGPT，讓團隊了解更新來源。',
      },
    ],
    faqTitle: '開始對話前，您可能想了解',
    faqs: [
      {
        question: '這項整合是給員工還是顧客使用？',
        answer:
          '這項整合適用於經營者與員工，連結時必須在所選 OmceanBooking 商家中具有有效員工角色。網站與 LINE 上的顧客預約對話，則是 OmceanBooking 的其他功能。',
      },
      {
        question: '可以連結多個商家嗎？',
        answer:
          '每個已連結帳號只會綁定一個商家。請分別連結各商家，並在提問或核准變更前確認目前使用的帳號。',
      },
      {
        question: '哪些操作無法透過 ChatGPT 執行？',
        answer:
          '此連接器不提供財務餘額或付款變更、員工存取權限變更、自由格式顧客訊息，以及取消訂閱等操作。顧客專屬的健康欄位也無法存取。可使用的工具取決於您的角色、預約模組與所選存取層級。',
      },
      {
        question: '之後可以中斷連線嗎？',
        answer:
          '可以。在 ChatGPT 開啟 Settings → Plugins → OmceanBooking，即可查看已連結帳號或中斷連線。中斷連線會阻止之後的存取，但不會復原您先前已核准的變更。',
      },
      {
        question: '如果找不到或無法使用整合功能怎麼辦？',
        answer:
          '先確認您的 ChatGPT 帳號或工作區可以使用外掛程式。使用具有有效員工角色的 OmceanBooking 帳號登入，選擇正確商家，並確認存取層級。您也可以請帳號管理員協助檢查角色權限。',
      },
    ],
    closingTitle: '下一個營運問題，直接問 ChatGPT。',
    closingText: '連結帳號、選擇權限，從今天的商家日常開始提問。',
    newCustomer: '還沒使用 OmceanBooking？',
    contact: '與我們的團隊聊聊',
  },
};
